export type PageOpenOutcome = 'ready' | 'error' | 'left' | 'hidden';
export type PageAbnormalReason = 'blank' | 'crash' | 'js_error' | 'api_5xx';
export type RumPageOutcome = {
    readonly page: string;
    readonly finishedMonoMS: number;
} & (
    | { readonly outcome: 'error'; readonly errorSource: 'render_boundary' | 'page_data' }
    | { readonly outcome: 'left'; readonly exitedCore?: true }
    | { readonly outcome: 'ready' | 'hidden' }
);
type Context = Record<string, unknown>;

interface PageLifecycleDependencies {
    initialPage: string;
    navigationStart?: (key: string) => number | undefined;
    now: () => number;
    captureGeneration: () => number | null;
    isGenerationCurrent: (generation: number) => boolean;
    reportAction: (name: string, context: Context) => unknown;
    reportError: (error: unknown, context: Context) => unknown;
    startView: (page: string) => void;
    createId: () => string;
    onSettled?: () => void;
}

interface PageVisit {
    key: string;
    page: string;
    openId: string;
    startedAt: number;
    nav: 'initial' | 'route_change';
    generation: number | null;
    outcome?: PageOpenOutcome;
    abnormal?: PageAbnormalReason;
    blocked: boolean;
    boundaryReported?: boolean;
    pending: Array<() => unknown>;
}

function safely(run: () => unknown): void {
    try {
        void Promise.resolve(run()).catch(() => undefined);
    } catch {
        // 遥测不可改变页面渲染、错误恢复或 HTTP 业务结果。
    }
}

export function isCoreRumPage(page: string): boolean {
    return (
        page === '/project/?' ||
        /^\/workspace\/\?\/(newchat|projects|connectors|experts|design-kit|infra|onboarding)$/.test(
            page
        )
    );
}

function isCoreExit(page?: string): boolean {
    return !!page && !isCoreRumPage(page) && page !== '/' && page !== '/workspace';
}

/** 只保存当前访问的已锁定结果；不跨路由积压、重试或持久化事件。 */
export class RumPageLifecycle {
    private visit: PageVisit | null = null;
    private currentKey: string | null = null;
    private documentStartAvailable: boolean;
    private redirectStartedAt: number | undefined;
    private armed = false;
    private denied = false;
    private latestOutcome: RumPageOutcome | null = null;
    private outcomeListeners = new Set<() => void>();

    constructor(private readonly deps: PageLifecycleDependencies) {
        this.documentStartAvailable =
            deps.initialPage === '/workspace' || isCoreRumPage(deps.initialPage);
    }

    getOutcome = (): RumPageOutcome | null => this.latestOutcome;

    subscribeOutcome = (listener: () => void): (() => void) => {
        this.outcomeListeners.add(listener);
        return () => { this.outcomeListeners.delete(listener); };
    };

    setOnSettled(callback: (() => void) | undefined): void {
        this.deps.onSettled = callback;
    }

    open(key: string, page: string, hidden = false): boolean {
        if (key === this.currentKey) return false;
        this.observeOutcome('left', 'page_data', page);
        this.currentKey = key;
        const navigationStart = this.deps.navigationStart?.(key) ?? this.deps.now();
        const startedAt = this.documentStartAvailable
            ? 0
            : this.redirectStartedAt ?? navigationStart;
        this.redirectStartedAt = page === '/workspace' ? navigationStart : undefined;
        safely(() => this.deps.startView(page));
        this.visit = isCoreRumPage(page)
            ? {
                  key,
                  page,
                  openId: this.deps.createId(),
                  startedAt,
                  nav: this.documentStartAvailable ? 'initial' : 'route_change',
                  generation: this.deps.captureGeneration(),
                  blocked: this.denied,
                  pending: [],
              }
            : null;
        // 只有直接进入产品及其入口中转可继承文档起点，官网停留不属于加载。
        if (this.visit || page !== '/workspace') {
            this.documentStartAvailable = false;
        }
        if (hidden) this.finish('hidden');
        return true;
    }

    get current() {
        return this.visit;
    }

    setArmed(armed: boolean): void {
        this.armed = armed;
        this.flush();
    }

    setConsent(state: 'pending' | 'granted' | 'denied'): void {
        this.denied = state === 'denied';
        if (state === 'denied') this.invalidate();
        if (state === 'granted' && this.visit && !this.visit.blocked) {
            this.visit.generation ??= this.deps.captureGeneration();
            this.flush();
        }
    }

    invalidate(): void {
        if (!this.visit) return;
        this.visit.blocked = true;
        this.visit.pending = [];
        this.deps.onSettled?.();
    }

    private enqueue(run: () => unknown): void {
        if (!this.visit || this.visit.blocked) return;
        this.visit.pending.push(run);
        this.flush();
    }

    private flush(): void {
        const visit = this.visit;
        if (!visit || visit.blocked || !this.armed || visit.generation === null) return;
        if (!this.deps.isGenerationCurrent(visit.generation)) {
            this.invalidate();
            return;
        }
        visit.pending.splice(0).forEach(run => safely(run));
    }

    finish(outcome: PageOpenOutcome, errorSource: 'render_boundary' | 'page_data' = 'page_data', nextPage?: string): void {
        const visit = this.visit;
        if (!visit || visit.outcome || visit.blocked) return;
        const waitMs = Math.max(0, this.deps.now() - visit.startedAt);
        visit.outcome = outcome;
        this.publishOutcome(visit.page, outcome, visit.startedAt + waitMs, errorSource, nextPage);
        const context = {
            page: visit.page,
            open_id: visit.openId,
            outcome: visit.outcome,
            wait_ms: waitMs,
            nav: visit.nav,
            metric_version: 2,
        };
        this.enqueue(() => this.deps.reportAction('route_open', context));
        this.deps.onSettled?.();
    }

    /** 真实业务信号仍可被新账号消费；不重开或重发已经结算的 RUM 访问。 */
    observeOutcome(outcome: PageOpenOutcome, errorSource: 'render_boundary' | 'page_data' = 'page_data', nextPage?: string): void {
        const visit = this.visit;
        if (!visit) return;
        const previous = this.latestOutcome;
        this.finish(outcome, errorSource, nextPage);
        if (this.latestOutcome === previous && (outcome !== 'left' || isCoreExit(nextPage))) {
            this.publishOutcome(visit.page, outcome, this.deps.now(), errorSource, nextPage);
        }
    }

    private publishOutcome(page: string, outcome: PageOpenOutcome, finishedMonoMS: number,
        errorSource: 'render_boundary' | 'page_data', nextPage?: string): void {
        let result: RumPageOutcome;
        if (outcome === 'error') result = { page, finishedMonoMS, outcome, errorSource };
        else if (outcome === 'left' && isCoreExit(nextPage)) {
            result = { page, finishedMonoMS, outcome, exitedCore: true };
        } else result = { page, finishedMonoMS, outcome };
        this.latestOutcome = Object.freeze(result);
        this.outcomeListeners.forEach(listener => safely(listener));
    }

    abnormal(reason: PageAbnormalReason): void {
        const visit = this.visit;
        if (!visit || visit.abnormal || visit.blocked) return;
        visit.abnormal = reason;
        const context = { page: visit.page, open_id: visit.openId, reason, metric_version: 2 };
        this.enqueue(() => this.deps.reportAction('page_abnormal', context));
    }

    boundary(
        error: unknown,
        boundary: 'route' | 'region',
        region: string,
        blocking: boolean
    ): void {
        const visit = this.visit;
        if (!visit) return;
        if (visit.blocked) {
            if (blocking) this.observeOutcome('error', 'render_boundary');
            return;
        }
        this.abnormal('crash');
        if (blocking) this.observeOutcome('error', 'render_boundary');
        if (visit.boundaryReported) return;
        visit.boundaryReported = true;
        const context = {
            page: visit.page,
            open_id: visit.openId,
            error_kind: 'react_boundary',
            boundary,
            region,
        };
        this.enqueue(() => this.deps.reportError(error, context));
    }

    dispose(): void {
        this.finish('left');
        this.visit = null;
        this.currentKey = null;
    }
}

/** 只认可明确主内容区域的实质空白；未知 DOM 和业务占位都不推断为白屏。 */
export function isRumMainContentBlank(root: HTMLElement | null): boolean {
    if (!root || root.hidden || root.getAttribute('aria-hidden') === 'true') return false;
    const bounds = root.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) return false;
    const style = getComputedStyle(root);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0')
        return false;
    if (
        root.matches('[data-rum-loading], [aria-busy="true"]') ||
        root.querySelector(
            '[data-rum-loading], [aria-busy="true"], [role="progressbar"], [data-rum-content]'
        )
    )
        return false;
    if (root.textContent?.trim()) return false;
    if (
        root.querySelector(
            'input, textarea, button, [contenteditable="true"], img, svg, canvas, video, iframe, [role="status"], [role="alert"]'
        )
    )
        return false;
    // 有结构但无法判定用途时保持 unknown，避免把 CSS 骨架当空白。
    return root.childElementCount === 0;
}

export function isActionableUnhandledError(value: unknown): boolean {
    if (!value || (typeof value !== 'object' && typeof value !== 'string')) return false;
    const candidate = value as {
        name?: string;
        message?: string;
        status?: number;
        code?: string;
        handled?: boolean;
    };
    if (candidate.handled || (candidate.status !== undefined && candidate.status < 500))
        return false;
    if (
        ['AbortError', 'CanceledError', 'AuthenticationRequiredError', 'NotAllowedError'].includes(
            candidate.name ?? ''
        )
    )
        return false;
    if (['ERR_CANCELED', 'ECONNABORTED'].includes(candidate.code ?? '')) return false;
    const message = typeof value === 'string' ? value : (candidate.message ?? '');
    if (
        !message ||
        /^(Script error\.?|ResizeObserver loop (limit exceeded|completed with undelivered notifications)\.?)$/i.test(
            message
        )
    )
        return false;
    if (
        /extension:\/\/|chrome-extension|moz-extension|user (cancelled|canceled|rejected)|request (aborted|cancelled|canceled)|insufficient (credits|balance)|payment required/i.test(
            message
        )
    )
        return false;
    return true;
}

/** 只采集已核验的业务 API 请求；未知来源和后台诊断请求均忽略。 */
export function isPageApi5xx(error: unknown, page: string, pathname: string): boolean {
    const candidate = error as {
        status?: number;
        cause?: {
            name?: string;
            code?: string;
            config?: { url?: string; signal?: { aborted?: boolean } };
        };
    } | null;
    if (!candidate || !candidate.status || candidate.status < 500 || candidate.status > 599)
        return false;
    const config = candidate.cause?.config;
    if (!config?.url || config.signal?.aborted || candidate.cause?.code === 'ERR_CANCELED')
        return false;
    let path: string;
    try {
        path = new URL(config.url, 'https://enter.invalid').pathname;
    } catch {
        return false;
    }
    if (/heartbeat|poll|telemetry|analytics\/events|logs|tracking/i.test(path)) return false;
    if (page === '/project/?') {
        const projectId = /^\/project\/([^/]+)/.exec(pathname)?.[1];
        return Boolean(
            projectId &&
                path.split('/').includes(projectId) &&
                /project|chat|turn|message/i.test(path)
        );
    }
    const scopes: Record<string, RegExp> = {
        '/workspace/?/newchat': /\/(projects?|agent[-_]?builders?)(\/|$)/,
        '/workspace/?/projects': /\/(project[-_]?items|projects?)(\/|$)/,
        '/workspace/?/connectors': /\/(plugins?|connections?|connectors?)(\/|$)/,
        '/workspace/?/experts': /\/(agent[-_]?templates?|skills?|models?|ai[-_]?capabilit)/,
        '/workspace/?/design-kit': /\/(marketplace|design[-_]?kits?|workspace[-_]?home)/,
        '/workspace/?/infra':
            /\/(cloud|instances?|knowledge[-_]?bases?|ai[-_]?all|analytics)(\/|$)/,
        '/workspace/?/onboarding': /\/onboarding(\/|$)/,
    };
    return scopes[page]?.test(path) ?? false;
}
